import { useQuery } from '@tanstack/react-query'
import { Users, CreditCard, Lightbulb } from 'lucide-react'
import { api } from '@/lib/api-client'
import { KpiCard } from '@/components/dashboard/kpi-card'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { formatCurrency, formatPercent, percent } from '@roi365/shared'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts'

interface Snapshot {
  total_users: number
  enabled_users: number
  licensed_users: number
  active_users_30d: number
  inactive_users_30d: number
  total_licenses_purchased: number
  total_licenses_assigned: number
  total_licenses_available: number
  estimated_monthly_cost: number
  assigned_monthly_cost: number
  estimated_monthly_waste: number
}

interface LicenseBreakdown {
  sku_part_number: string
  display_name: string
  total_units: number
  assigned_units: number
  available_units: number
  monthly_cost_per_unit: number | null
  assigned_cost: number
  total_cost: number
}

const CHART_COLORS = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
  'var(--color-chart-6)',
]

export function DashboardPage() {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () =>
      api.get<{
        snapshot: Snapshot | null
        recommendations: { pendingCount: number; totalPotentialSaving: number; appliedSavingLastYear: number }
      }>('/dashboard/summary'),
  })

  const { data: costBreakdown } = useQuery({
    queryKey: ['dashboard', 'cost-breakdown'],
    queryFn: () => api.get<LicenseBreakdown[]>('/dashboard/cost-breakdown'),
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Dashboard" description="Vista general del estado de licencias Microsoft 365" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ))}
        </div>
      </div>
    )
  }

  const s = summary?.snapshot
  const r = summary?.recommendations

  // Prepare chart data
  const pieData = (costBreakdown ?? [])
    .filter((l) => l.assigned_units > 0)
    .sort((a, b) => b.assigned_units - a.assigned_units)
    .map((l) => ({
      name: l.display_name,
      value: l.assigned_units,
    }))

  const costCompareData = (costBreakdown ?? [])
    .filter((l) => l.total_cost > 0)
    .slice(0, 8)
    .map((l) => ({
      name: l.display_name,
      total: Math.round(l.total_cost * 100) / 100,
      asignadas: Math.round(l.assigned_cost * 100) / 100,
    }))

  const hasData = s && (s.total_users > 0 || s.total_licenses_purchased > 0)

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description="Vista general del estado de licencias Microsoft 365" />

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/* Usuarios: total + inactivos en un solo card */}
        <div
          className="rounded-lg border p-5"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
            borderLeftWidth: '4px',
            borderLeftColor: 'var(--color-brand-primary)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Usuarios</p>
              <p className="mt-1 text-3xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {s?.total_users ?? 0}
              </p>
            </div>
            <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
              <Users className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span style={{ color: 'var(--color-success)' }}>
              <span className="font-semibold">{s?.active_users_30d ?? 0}</span> activos
            </span>
            <span style={{ color: 'var(--color-warning)' }}>
              <span className="font-semibold">{s?.inactive_users_30d ?? 0}</span> inactivos 30d ({formatPercent(percent(s?.inactive_users_30d ?? 0, s?.total_users ?? 1))})
            </span>
          </div>
        </div>

        <KpiCard
          label="Licencias Asignadas"
          value={`${s?.total_licenses_assigned ?? 0} / ${s?.total_licenses_purchased ?? 0}`}
          subtitle={`${formatPercent(percent(s?.total_licenses_assigned ?? 0, s?.total_licenses_purchased ?? 1))} utilizacion`}
          icon={CreditCard}
          accentColor="var(--color-brand-secondary)"
        />
        <KpiCard
          label="Coste Mensual Total"
          value={formatCurrency(s?.estimated_monthly_cost ?? 0)}
          subtitle={`${formatCurrency(s?.assigned_monthly_cost ?? 0)} en asignadas`}
          icon={CreditCard}
          accentColor="var(--color-brand-accent-1)"
        />
        <div
          className="rounded-lg border p-5"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
            borderLeftWidth: '4px',
            borderLeftColor: 'var(--color-brand-accent-2)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Ahorro Potencial Estimado</p>
              <p className="mt-1 text-3xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {formatCurrency(r?.totalPotentialSaving ?? 0)}
              </p>
              <p className="mt-1 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                {r?.pendingCount ?? 0} recomendaciones
              </p>
            </div>
            <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
              <Lightbulb className="h-5 w-5" />
            </div>
          </div>
          {(r?.appliedSavingLastYear ?? 0) > 0 && (
            <div className="mt-3 text-sm" style={{ color: 'var(--color-success)' }}>
              <span className="font-semibold">{formatCurrency(r!.appliedSavingLastYear)}</span> ahorro proyectado desde aplicación
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      {hasData ? (
        <>
          {/* Coste por licencia: total vs asignadas (ancho completo) */}
          <Card>
            <CardHeader><CardTitle>Coste Mensual por Licencia</CardTitle></CardHeader>
            {costCompareData.length > 0 ? (
              <div style={{ height: Math.max(280, costCompareData.length * 50) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costCompareData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--color-text-tertiary)" tickFormatter={(v) => `${v}€`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="var(--color-text-tertiary)" width={200} />
                    <Tooltip formatter={(value: number, name: string) => [
                      formatCurrency(value),
                      name === 'Coste total' ? 'Coste total (compradas)' : 'Coste asignadas',
                    ]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="total" name="Coste total" fill="var(--color-brand-primary)" radius={[0, 4, 4, 0]} opacity={0.4} />
                    <Bar dataKey="asignadas" name="Coste asignadas" fill="var(--color-brand-secondary)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Configura precios de licencias para ver el desglose
              </p>
            )}
          </Card>

          {/* Distribución de licencias */}
          <Card>
            <CardHeader><CardTitle>Distribucion de Licencias</CardTitle></CardHeader>
            {pieData.length > 0 ? (
              <div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number, name: string) => [`${value} licencias`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 pb-2">
                  {pieData.map((entry, i) => (
                    <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span style={{ color: 'var(--color-text-secondary)' }}>{entry.name}</span>
                      <span className="tabular-nums font-medium" style={{ color: 'var(--color-text-primary)' }}>({entry.value})</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Sin datos de licencias aun
              </p>
            )}
          </Card>
        </>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <CreditCard className="mb-3 h-10 w-10" style={{ color: 'var(--color-text-tertiary)' }} />
              <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Conecta un tenant M365 para ver graficos
              </p>
              <p className="mt-1 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Ve a Tenants M365 y conecta tu primer tenant
              </p>
            </div>
          </Card>
          <Card>
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <Lightbulb className="mb-3 h-10 w-10" style={{ color: 'var(--color-text-tertiary)' }} />
              <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Recomendaciones automaticas
              </p>
              <p className="mt-1 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Las recomendaciones se generan tras la primera sincronizacion
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Cost summary */}
      {s && s.estimated_monthly_cost > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Coste mensual total</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(s.estimated_monthly_cost)}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Coste licencias asignadas</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-brand-secondary)' }}>
              {formatCurrency(s.assigned_monthly_cost ?? 0)}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Desperdicio estimado</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-error)' }}>
              {formatCurrency(s.estimated_monthly_cost - (s.assigned_monthly_cost ?? 0))}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Coste anualizado</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(s.estimated_monthly_cost * 12)}
            </p>
          </Card>
        </div>
      )}
    </div>
  )
}
